import { useState, useEffect } from "react";
import { langPath, switchLangUrl, saveLangPref } from "./utils/lang";
import { setPageMeta } from "./utils/meta";

export default function Contact({ language = 'th' }) {
  const [formData, setFormData] = useState({
    company: "", name: "", email: "", phone: "", message: "", privacy: false,
  });
  const [status, setStatus] = useState(null);

  useEffect(() => { setPageMeta(language, '/contact'); }, [language]);

  const root = langPath(language, '/');
  const privacy = langPath(language, '/privacy');
  const isEn = language === 'en';
  const isTh = language === 'th';

  /* 3言語切替ヘルパー */
  const t = (th, en, jp) => isTh ? th : isEn ? en : jp;

  const tr = {
    backLink: t("← กลับไปหน้าหลัก", "← Back to Top", "← トップに戻る"),
    pageTitle: t("ปรึกษาฟรี / ติดต่อเรา", "Contact Us", "無料相談・お問い合わせ"),
    labelCompany: t("ชื่อบริษัท", "Company Name", "会社名"),
    labelName: t("ชื่อผู้ติดต่อ", "Contact Person", "担当者名"),
    labelEmail: t("อีเมล", "Email Address", "メールアドレス"),
    labelPhone: t("เบอร์โทรศัพท์", "Phone Number", "電話番号"),
    labelMessage: t("รายละเอียดที่ต้องการสอบถาม", "Message", "お問い合わせ内容"),
    required: t("จำเป็น", "Required", "必須"),
    optional: t("ไม่บังคับ", "Optional", "任意"),
    phCompany: t("ตัวอย่าง: ABC Clinic Co., Ltd.", "Example: ABC Clinic Co., Ltd.", "例：ABC Clinic Co., Ltd."),
    phName: t("ตัวอย่าง: Tanaka", "Example: Tanaka", "例：Tanaka"),
    phEmail: "example@company.com",
    phPhone: "+66 80 000 0000",
    phMessage: t("กรุณาระบุเรื่องที่ต้องการปรึกษา วัตถุประสงค์การใช้งาน หรือสิ่งที่กำลังมองหา", "Please tell us about your inquiry, intended use, or current needs.", "ご相談内容・利用目的などをご記入ください"),
    privacyConsent: isEn ? "I agree to the " : "",
    privacyConsentAfter: isEn ? "." : "に同意する",
    privacyAlert: t("กรุณายอมรับนโยบายความเป็นส่วนตัว", "Please agree to the Privacy Policy.", "プライバシーポリシーに同意してください。"),
    submitBtn: t("ส่งข้อความ", "Send Message", "メッセージを送信"),
    submitting: t("กำลังส่ง...", "Sending...", "送信中..."),
    successTitle: t("ส่งข้อความเรียบร้อยแล้ว", "Message Sent", "送信完了しました"),
    successBody1: t("ขอบคุณที่ติดต่อเรา", "Thank you for contacting us.", "お問い合わせありがとうございます。"),
    successBody2: t("ทีมงานจะตรวจสอบรายละเอียดและติดต่อกลับโดยเร็ว", "Our team will review your inquiry and get back to you shortly.", "内容を確認の上、担当者よりご連絡いたします。"),
    errorMsg: t("ส่งข้อความไม่สำเร็จ กรุณาลองใหม่อีกครั้งภายหลัง", "Failed to send your message. Please try again later.", "送信に失敗しました。時間をおいて再度お試しください。"),
    sidebarTitle: t("ปรึกษาเรื่องการใช้ Nunt", "Talk to us about using Nunt", "Nuntの活用についてご相談ください"),
    sidebarBody: t("สามารถติดต่อเราได้เกี่ยวกับการใช้ภาพทาเลนต์ แพ็กเกจราคา หรือตัวอย่างการใช้งานที่เหมาะกับธุรกิจของคุณ", "Please feel free to contact us about talent visual usage, pricing plans, or possible use cases.", "タレント素材の利用、料金プラン、活用イメージについて、お気軽にお問い合わせください。"),
    companyLabel: t("บริษัท", "Company", "運営会社"),
    addressLabel: t("ที่อยู่", "Address", "所在地"),
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacy) {
      alert(tr.privacyAlert);
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/mojpbped", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          _subject: "【Nunt】お問い合わせが届きました",
          form_type: "contact",
          language: language,
          会社名: formData.company,
          担当者名: formData.name,
          メールアドレス: formData.email,
          電話番号: formData.phone || "未記入",
          お問い合わせ内容: formData.message,
        }),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ company: "", name: "", email: "", phone: "", message: "", privacy: false });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const inputStyle = {
    width: "100%", padding: "13px 14px",
    border: "1px solid rgba(6, 24, 69, 0.14)", borderRadius: 8,
    fontSize: 14, color: "#001A3D",
    background: "#fff",
    outline: "none", fontFamily: "'Noto Sans JP', sans-serif",
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
  };

  const labelStyle = {
    fontSize: 13, fontWeight: 800,
    color: "#061845", marginBottom: 8,
    display: "block",
  };

  return (
    <div className="contact-page" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#001A3D", background: "#fff", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .contact-page {
          background:
            radial-gradient(circle at 80% 8%, rgba(116, 105, 216, 0.12) 0, rgba(116, 105, 216, 0) 32%),
            linear-gradient(180deg, #fff 0%, #fbfaff 100%) !important;
        }
        .contact-page input::placeholder,
        .contact-page textarea::placeholder {
          color: #939bb1;
        }
        .contact-page input:focus,
        .contact-page textarea:focus {
          border-color: #d8a132 !important;
          box-shadow: 0 0 0 3px rgba(216, 161, 50, 0.14);
        }
        .contact-back-link,
        .contact-privacy-link,
        .contact-submit-button {
          transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
        }
        .contact-back-link:hover,
        .contact-privacy-link:hover {
          color: #b88418 !important;
        }
        .contact-submit-button:not(:disabled):hover {
          background: linear-gradient(135deg, #b96e07 0%, #d79a24 48%, #a96306 100%) !important;
          box-shadow: 0 10px 22px rgba(184, 132, 24, 0.18) !important;
          transform: translateY(-1px);
        }
        .contact-lang-btn {
          font-size: 11px; font-weight: 600;
          color: #bbb; text-decoration: none; padding: 4px 4px;
          line-height: 1; transition: color 0.15s ease;
        }
        .contact-lang-btn:hover { color: #061845; }
        .contact-lang-btn.is-active {
          font-weight: 900; color: #061845;
          text-decoration: underline; text-underline-offset: 2px;
        }
        @media (max-width: 768px) {
          .contact-header {
            padding: 12px 18px !important;
          }
          .contact-logo {
            height: 66px !important;
          }
          .contact-main {
            padding: 42px 18px 52px !important;
          }
          .contact-title {
            font-size: 25px !important;
            margin-bottom: 28px !important;
          }
          .contact-content {
            gap: 22px !important;
          }
          .contact-form-card,
          .contact-info-card {
            padding: 22px !important;
          }
          .contact-info-card {
            flex-basis: 100% !important;
          }
        }
      `}</style>

      {/* ヘッダー */}
      <header className="contact-header" style={{
        background: "rgba(255, 255, 255, 0.95)", borderBottom: "1px solid rgba(16, 30, 75, 0.08)",
        padding: "16px 24px", display: "flex", alignItems: "center",
        boxShadow: "0 10px 28px rgba(33, 26, 82, 0.04)",
      }}>
        <a href={root} style={{ textDecoration: "none" }}>
          <img className="contact-logo" src="/nuntlogo.png" alt="Nunt" style={{ height: 90, width: "auto" }} />
        </a>

        {/* 言語切替 */}
        <div style={{ display: "flex", alignItems: "center", gap: 3, marginLeft: "auto", marginRight: 14 }}>
          {[['th','TH'],['en','EN'],['jp','JP']].map(([l, label], i) => (
            <span key={l} style={{ display: "flex", alignItems: "center" }}>
              {i > 0 && <span style={{ color: "#d0d0d0", fontSize: 10, margin: "0 2px" }}>/</span>}
              <a
                className={`contact-lang-btn${language === l ? " is-active" : ""}`}
                href={switchLangUrl(l)}
                onClick={() => saveLangPref(l)}
              >{label}</a>
            </span>
          ))}
        </div>

        <a className="contact-back-link" href={root} style={{
          fontSize: 13, color: "#061845",
          textDecoration: "none", fontWeight: 700,
          display: "flex", alignItems: "center", gap: 4,
        }}>
          {tr.backLink}
        </a>
      </header>

      {/* コンテンツ */}
      <main className="contact-main" style={{ maxWidth: 1040, margin: "0 auto", padding: "64px 24px 72px" }}>
        <h1 className="contact-title" style={{
          fontSize: 30, fontWeight: 900, color: "#061845",
          marginBottom: 48, paddingBottom: 16,
          borderBottom: "2px solid rgba(216, 161, 50, 0.72)",
          lineHeight: 1.4,
        }}>
          {tr.pageTitle}
        </h1>

        <div className="contact-content" style={{ display: "flex", gap: 34, alignItems: "flex-start", flexWrap: "wrap" }}>

          {/* 左：フォーム */}
          <div className="contact-form-card" style={{
            flex: "1 1 520px", minWidth: 0,
            background: "rgba(255, 255, 255, 0.95)",
            border: "1px solid rgba(6, 24, 69, 0.1)",
            borderRadius: 8,
            padding: 30,
            boxShadow: "0 14px 34px rgba(32, 28, 84, 0.06)",
          }}>
            {status === "success" ? (
              <div style={{
                background: "#fbfaff", border: "1px solid rgba(116, 105, 216, 0.24)",
                borderRadius: 12, padding: "32px 24px", textAlign: "center",
              }}>
                <p style={{ fontSize: 20, fontWeight: 900, color: "#061845", marginBottom: 12 }}>
                  {tr.successTitle}
                </p>
                <p style={{ fontSize: 14, color: "#444", lineHeight: 1.8 }}>
                  {tr.successBody1}<br />
                  {tr.successBody2}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ display: "flex", gap: 20, marginBottom: 20, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={labelStyle}>{tr.labelCompany} <span style={{ color: "#e02020" }}>*</span></label>
                    <input
                      style={inputStyle} type="text" name="company"
                      value={formData.company} onChange={handleChange}
                      placeholder={tr.phCompany} required
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={labelStyle}>{tr.labelName} <span style={{ color: "#e02020" }}>*</span></label>
                    <input
                      style={inputStyle} type="text" name="name"
                      value={formData.name} onChange={handleChange}
                      placeholder={tr.phName} required
                    />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 20, marginBottom: 20, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={labelStyle}>{tr.labelEmail} <span style={{ color: "#e02020" }}>*</span></label>
                    <input
                      style={inputStyle} type="email" name="email"
                      value={formData.email} onChange={handleChange}
                      placeholder={tr.phEmail} required
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={labelStyle}>{tr.labelPhone} <span style={{ color: "#888", fontWeight: 400 }}>{isTh ? `(${tr.optional})` : isEn ? "(Optional)" : "（任意）"}</span></label>
                    <input
                      style={inputStyle} type="tel" name="phone"
                      value={formData.phone} onChange={handleChange}
                      placeholder={tr.phPhone}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>{tr.labelMessage} <span style={{ color: "#e02020" }}>*</span></label>
                  <textarea
                    style={{ ...inputStyle, height: 160, resize: "vertical" }}
                    name="message" value={formData.message}
                    onChange={handleChange}
                    placeholder={tr.phMessage}
                    required
                  />
                </div>

                <div style={{ marginBottom: 28, display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <input
                    type="checkbox" name="privacy" id="privacy"
                    checked={formData.privacy} onChange={handleChange}
                    style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                  />
                  <label htmlFor="privacy" style={{ fontSize: 13, color: "#444", cursor: "pointer", lineHeight: 1.7 }}>
                    {isTh ? (
                      <>
                        ฉันยอมรับ{" "}
                        <a className="contact-privacy-link" href={privacy} style={{ color: "#061845", textDecoration: "underline", textUnderlineOffset: 3 }}>นโยบายความเป็นส่วนตัว</a>
                      </>
                    ) : isEn ? (
                      <>
                        I agree to the{" "}
                        <a className="contact-privacy-link" href={privacy} style={{ color: "#061845", textDecoration: "underline", textUnderlineOffset: 3 }}>Privacy Policy</a>.
                      </>
                    ) : (
                      <>
                        <a className="contact-privacy-link" href={privacy} style={{ color: "#061845", textDecoration: "underline", textUnderlineOffset: 3 }}>プライバシーポリシー</a>
                        に同意する
                      </>
                    )}
                  </label>
                </div>

                <button
                  className="contact-submit-button"
                  type="submit"
                  disabled={status === "sending"}
                  style={{
                    width: "100%", padding: "14px",
                    background: status === "sending" ? "#cfd3dc" : "linear-gradient(135deg, #c77b0a 0%, #e0a127 48%, #bf7107 100%)",
                    color: "#fff", border: "none", borderRadius: 8,
                    boxShadow: status === "sending" ? "none" : "0 8px 18px rgba(192, 113, 7, 0.14)",
                    fontSize: 15, fontWeight: 700, cursor: status === "sending" ? "not-allowed" : "pointer",
                  }}
                >
                  {status === "sending" ? tr.submitting : tr.submitBtn}
                </button>

                {status === "error" && (
                  <p style={{ color: "#e02020", fontSize: 13, marginTop: 12, textAlign: "center" }}>
                    {tr.errorMsg}
                  </p>
                )}
              </form>
            )}
          </div>

          {/* 右：会社情報 */}
          <div className="contact-info-card" style={{
            flex: "0 0 320px", minWidth: 260,
            background: "rgba(255, 255, 255, 0.82)",
            border: "1px solid rgba(116, 105, 216, 0.14)",
            borderRadius: 8,
            padding: 28,
            boxShadow: "0 10px 26px rgba(32, 28, 84, 0.04)",
          }}>
            <h2 style={{ fontSize: 18, fontWeight: 900, color: "#061845", marginBottom: 16, lineHeight: 1.55 }}>
              {tr.sidebarTitle}
            </h2>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.9, marginBottom: 24 }}>
              {tr.sidebarBody}
            </p>
            <div style={{ borderTop: "1px solid rgba(6, 24, 69, 0.1)", paddingTop: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#888", marginBottom: 6 }}>{tr.companyLabel}</p>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#001A3D", marginBottom: 12 }}>Patlush Co., Ltd.</p>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#888", marginBottom: 6 }}>{tr.addressLabel}</p>
              <p style={{ fontSize: 13, color: "#444", lineHeight: 1.8, marginBottom: 12 }}>
                388 Exchange Tower, 29th Fl., 42nd Fl.,<br />
                Unit 2901-2904, 4201-4204,<br />
                Sukhumvit Rd., Klongtoey District,<br />
                Bangkok 10110 Thailand
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* フッター */}
      <footer style={{ background: "#F8F9FA", borderTop: "1px solid #e8e8e8", padding: "24px", textAlign: "center" }}>
        <p style={{ fontSize: 12, color: "#888" }}>© 2026 Patlush All Rights Reserved.</p>
      </footer>
    </div>
  );
}
